import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { ADMIN_BASE_URL } from "../config/config";
import {
  getAdminUserListCacheAge,
  getCachedAdminUserList,
  normalizeAdminUsers,
  setCachedAdminUserList,
} from "../utils/adminUserListCache";

const getAuthHeaders = () => {
  const token = localStorage.getItem("care_admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const useAdminUserList = () => {
  const [country, setCountry] = useState("");
  const [id, setId] = useState("");
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [loadedFromCache, setLoadedFromCache] = useState(false);
  const countryRef = useRef(country);
  const idRef = useRef(id);

  countryRef.current = country;
  idRef.current = id;

  const fetchUsers = useCallback(async ({ force = false, filters } = {}) => {
    const activeCountry = filters?.country ?? countryRef.current;
    const activeId = filters?.id ?? idRef.current;

    if (!force) {
      const cached = getCachedAdminUserList(activeCountry, activeId);
      if (cached) {
        setUsers(cached.users);
        setLoadedFromCache(true);
        setLoadError("");
        return cached.users;
      }
    }

    setLoadingUsers(true);
    setLoadError("");
    setLoadedFromCache(false);

    try {
      const response = await axios.get(`${ADMIN_BASE_URL}/filter`, {
        params: {
          name: activeCountry || undefined,
          id: activeId || undefined,
        },
        headers: getAuthHeaders(),
      });

      const normalized = normalizeAdminUsers(response.data);
      setUsers(normalized);
      setCachedAdminUserList(activeCountry, activeId, normalized);
      return normalized;
    } catch (error) {
      console.error("Error loading users:", error);
      setUsers([]);
      setLoadError(
        error.response?.data?.msg ||
          error.response?.data?.error ||
          "Unable to load users. Please try again."
      );
      return [];
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const applyFilters = useCallback(() => {
    fetchUsers({
      force: true,
      filters: { country: countryRef.current, id: idRef.current },
    });
  }, [fetchUsers]);

  const refreshUsers = useCallback(() => {
    fetchUsers({
      force: true,
      filters: { country: countryRef.current, id: idRef.current },
    });
  }, [fetchUsers]);

  const cacheAgeMs = getAdminUserListCacheAge(country, id);

  return {
    country,
    setCountry,
    id,
    setId,
    users,
    loadingUsers,
    loadError,
    loadedFromCache,
    cacheAgeMs,
    applyFilters,
    refreshUsers,
  };
};
